import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Moon, Sun, Flame, Upload, ImagePlus, Check, X, Loader2, Wand2 } from "lucide-react";
import { GuidelinesLink } from "@/components/GuidelinesLink";

const ICONS = { sparkles: Sparkles, moon: Moon, sun: Sun, flame: Flame };
const ACCENT = { spark: "#ffd600", glow: "#22c3d6", shine: "#ff9f1c", radiate: "#ff6a13" };

export default function RequestDesign() {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fileRef = useRef(null);
  const [tiers, setTiers] = useState([]);
  const [tierKey, setTierKey] = useState(params.get("tier") || "");
  const serviceName = params.get("service") || "";
  const [mode, setMode] = useState("upload");
  const [catalog, setCatalog] = useState([]);
  const [images, setImages] = useState([]); // [{url}]
  const [baseUrl, setBaseUrl] = useState("");
  const [details, setDetails] = useState(params.get("service") ? `Quote request for: ${params.get("service")}\n\n` : "");
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/design-tiers").then((r) => setTiers(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (mode === "catalog" && catalog.length === 0) api.get("/designs").then((r) => setCatalog(r.data || [])).catch(() => {}); }, [mode, catalog.length]);

  const tier = tiers.find((t) => t.key === tierKey);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > 5) { toast.error("Up to 5 inspiration images"); return; }
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((p) => [...p, { url: data.url }]);
        setBaseUrl((b) => b || data.url);
      } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const toggleGallery = (url) => {
    setImages((p) => {
      const has = p.some((i) => i.url === url);
      if (has) { if (baseUrl === url) setBaseUrl(""); return p.filter((i) => i.url !== url); }
      if (p.length >= 5) { toast.error("Up to 5 inspiration images"); return p; }
      setBaseUrl((b) => b || url);
      return [...p, { url }];
    });
  };

  const removeImage = (url) => { setImages((p) => p.filter((i) => i.url !== url)); if (baseUrl === url) setBaseUrl(""); };

  const addToCart = () => {
    if (!tier) { toast.error("Choose a design tier"); return; }
    if (images.length === 0 && !details.trim()) { toast.error("Add at least one inspiration image or some details"); return; }
    addItem({
      kind: "design_request",
      name: `Custom Design — ${tier.name}`,
      unit_price: tier.price, quantity: 1,
      tier_key: tier.key, revisions: tier.revisions,
      inspiration_images: images.map((i) => i.url),
      base_image_url: baseUrl || (images[0] && images[0].url) || null,
      details: details.trim() || null,
      preview_url: baseUrl || (images[0] && images[0].url) || null,
      notes: details.trim() ? `Design brief: ${details.trim().slice(0, 120)}` : null,
    });
    toast.success("Design request added to cart");
    navigate("/cart");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="request-design-page">
      <div className="flex items-center gap-2 text-[#ff6a13] font-mono-p text-xs uppercase tracking-widest"><Wand2 size={14} />Custom Design Service</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white mt-2">Request a Design</h1>
      <p className="text-zinc-400 mt-2 max-w-2xl">Pick a service tier, share reference images and a brief description, and our team will craft print-ready artwork. Add it to your cart and we'll take it from there.</p>

      {/* Tiers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {tiers.map((t) => {
          const Icon = ICONS[t.icon] || Sparkles;
          const accent = ACCENT[t.key] || "#ff6a13";
          const active = tierKey === t.key;
          return (
            <button key={t.key} onClick={() => setTierKey(t.key)} data-testid={`tier-${t.key}`}
              className={`text-left rounded-2xl p-5 border transition-all ${active ? "border-transparent ring-2 bg-[#161922]" : "border-zinc-800 bg-[#12141a] hover:border-zinc-700"}`}
              style={active ? { boxShadow: `0 0 0 2px ${accent}` } : {}}>
              <div className="flex items-center justify-between">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}><Icon size={20} /></span>
                {active && <Check size={18} style={{ color: accent }} />}
              </div>
              <p className="font-display font-bold text-white text-lg mt-3">{t.name}</p>
              <p className="text-[13px] text-zinc-400 mt-1 leading-snug">{t.description}</p>
              <p className="font-mono-p font-bold mt-3" style={{ color: accent }}>${t.price.toFixed(2)}</p>
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        {/* Inspiration */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-white">Inspiration images</h2>
              <div className="flex rounded-full bg-[#1e222d] p-0.5 text-xs">
                <button onClick={() => setMode("upload")} data-testid="mode-upload" className={`px-3 py-1.5 rounded-full font-semibold ${mode === "upload" ? "bg-[#ff6a13] text-white" : "text-zinc-400"}`}>Upload</button>
                <button onClick={() => setMode("catalog")} data-testid="mode-catalog" className={`px-3 py-1.5 rounded-full font-semibold ${mode === "catalog" ? "bg-[#ff6a13] text-white" : "text-zinc-400"}`}>From catalog</button>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-1">Add up to 5. Tap an image to mark it as the base to build on. <GuidelinesLink testid="request-guidelines-link" /></p>

            {mode === "upload" ? (
              <button onClick={() => fileRef.current?.click()} data-testid="upload-drop" className="mt-4 w-full border-2 border-dashed border-zinc-700 rounded-xl py-10 flex flex-col items-center justify-center text-zinc-400 hover:border-[#ff6a13] hover:text-white transition-colors">
                {uploading ? <Loader2 className="animate-spin" size={24} /> : <Upload size={24} />}
                <span className="text-sm mt-2 font-semibold">{uploading ? "Uploading…" : "Click to upload PNG / JPG"}</span>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg" multiple className="hidden" onChange={onUpload} data-testid="upload-input" />
              </button>
            ) : (
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-auto">
                {catalog.length === 0 && <p className="col-span-full text-sm text-zinc-500 py-6 text-center">No catalog designs available yet.</p>}
                {catalog.map((g) => {
                  const sel = images.some((i) => i.url === g.image_url);
                  return (
                    <button key={g.design_id || g.image_url} onClick={() => toggleGallery(g.image_url)} data-testid={`catalog-pick-${g.design_id || ""}`} title={g.title} className={`relative aspect-square rounded-lg overflow-hidden border-2 ${sel ? "border-[#ff6a13]" : "border-transparent"} checker`}>
                      <img src={fileUrl(g.image_url)} alt={g.title} className="w-full h-full object-contain" />
                      {sel && <span className="absolute top-1 right-1 bg-[#ff6a13] rounded-full p-0.5"><Check size={12} className="text-white" /></span>}
                    </button>
                  );
                })}
              </div>
            )}

            {images.length > 0 && (
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="selected-images">
                {images.map((im) => (
                  <div key={im.url} onClick={() => setBaseUrl(im.url)} className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer checker ${baseUrl === im.url ? "border-[#22c3d6]" : "border-zinc-800"}`}>
                    <img src={fileUrl(im.url)} alt="" className="w-full h-full object-contain" />
                    {baseUrl === im.url && <span className="absolute bottom-0 inset-x-0 bg-[#22c3d6] text-black text-[9px] font-bold text-center py-0.5">BASE</span>}
                    <button onClick={(e) => { e.stopPropagation(); removeImage(im.url); }} className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5 text-white hover:bg-red-500"><X size={11} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
            <h2 className="font-display font-bold text-white flex items-center gap-2"><ImagePlus size={16} className="text-[#ff6a13]" />Design brief & details</h2>
            <Textarea value={details} onChange={(e) => setDetails(e.target.value)} data-testid="design-details" rows={5} placeholder="Describe your idea — text/wording, colors, style, vibe, where it'll be printed, deadlines, anything else…" className="mt-3 bg-[#1e222d] border-zinc-700 text-white" />
          </div>
        </div>

        {/* Summary */}
        <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-6 h-fit lg:sticky lg:top-24">
          <h2 className="font-display text-lg font-bold text-white">Summary</h2>
          <div className="flex justify-between text-sm mt-4"><span className="text-zinc-400">Tier</span><span className="font-mono-p text-white">{tier ? tier.name : "—"}</span></div>
          <div className="flex justify-between text-sm mt-2"><span className="text-zinc-400">Revisions</span><span className="font-mono-p text-white">{tier ? tier.revisions : "—"}</span></div>
          <div className="flex justify-between text-sm mt-2"><span className="text-zinc-400">Inspiration</span><span className="font-mono-p text-white">{images.length} image{images.length === 1 ? "" : "s"}</span></div>
          <div className="border-t border-zinc-800 my-4" />
          <div className="flex justify-between items-center"><span className="text-zinc-300 font-semibold">Price</span><span className="font-mono-p font-bold text-2xl text-white">${tier ? tier.price.toFixed(2) : "0.00"}</span></div>
          <Button onClick={addToCart} data-testid="request-add-to-cart" className="w-full mt-5 bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-11 font-semibold">Add to Cart</Button>
          <p className="text-[11px] text-zinc-500 mt-3 text-center">Our team reviews your brief after checkout and shares drafts for your approval.</p>
        </div>
      </div>
    </div>
  );
}
